"use client";

import { searchHref } from "@/shared/routes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { getSuggestions, searchProducts } from "../services/search.service";
import type { SearchProductHit } from "../types/search.dto";
import { getActiveStoreId } from "@/modules/store/stores/useActiveStore";
import { searchHitSaleRules } from "@/modules/catalog";
import { useSearchHistory } from "./useSearchHistory";
import type { SearchResult, VisitedProduct } from "../types/search";

/**
 * Fonte do dropdown enquanto o usuário digita — UMA requisição por tecla:
 *
 * - "suggest": GET /search/suggest — leve, feito para autocomplete. Os termos
 *   sugeridos são os `terms` do motor (complementos do digitado sustentados
 *   pelo catálogo), com nomes de categoria como reforço.
 * - "search":  GET /search/products — a busca completa, paginada; sem termos.
 *
 * Estática por enquanto (pedido do produto); quando precisar variar por tema,
 * vira config do bootstrap sem mudar o resto deste arquivo.
 */
const DROPDOWN_SOURCE: "suggest" | "search" = "suggest";

/** Tamanho da página no modo "search" (o modo "suggest" é limitado pelo back). */
const DROPDOWN_PAGE_SIZE = 12;

/** Máximo de termos sugeridos no dropdown (o back manda até 6 + 4 categorias). */
const DROPDOWN_TERM_LIMIT = 6;

/** `terms` do motor primeiro (mais específicos), categorias completam — sem repetição. */
function mergeSuggestedTerms(terms: string[], categoryNames: string[]): string[] {
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const term of [...terms, ...categoryNames]) {
        const key = term.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(term);
        if (merged.length >= DROPDOWN_TERM_LIMIT) break;
    }
    return merged;
}

const DEBOUNCE_MS = 280;

function toSearchResult(hit: SearchProductHit): SearchResult {
    const offer = typeof hit.offerPrice === "number" && hit.offerPrice < hit.price;
    return {
        id: hit.productId,
        name: hit.name,
        brand: hit.brand,
        price: offer ? hit.offerPrice! : hit.price,
        oldPrice: offer ? hit.price : undefined,
        hasStock: hit.hasStock,
        imageUrl: hit.imageUrl,
        slug: hit.slug,
        // As regras reais: antes o dropdown hardcodava 1/1 e um produto vendido
        // de 5 em 5 kg chegava ao carrinho como unitário (400 na nest).
        saleRules: searchHitSaleRules(hit),
    };
}

export interface UseSearchControllerOptions {
    /** Notifica o valor digitado — usado por ações externas (ex.: modal fullscreen). */
    onValueChange?: (value: string) => void;
    /** Notifica os resultados carregados — usado para contextualizar CTAs externos. */
    onSearchResultsChange?: (results: SearchResult[] | null) => void;
    /** Valor inicial do campo (ex.: `?q=` da página de busca) — só lido no mount. */
    initialValue?: string;
}

/**
 * Estado/lógica de busca com dropdown de sugestões — extraído de `SearchBar`
 * para ser reaproveitado por qualquer lugar que precise do mesmo comportamento
 * (ex.: o slot `search.dropdown` do `Header` desktop). Digitando: uma chamada
 * ao back (ver DROPDOWN_SOURCE) com debounce. Idle: histórico (localStorage).
 */
export function useSearchController(options: UseSearchControllerOptions = {}) {
    const { onValueChange, onSearchResultsChange, initialValue = "" } = options;
    const router = useRouter();
    const { history, add, remove, refresh: refreshHistory } = useSearchHistory();

    const [value, setValue] = useState(initialValue);
    const [isOpen, setIsOpen] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [results, setResults] = useState<SearchResult[] | null>(null);
    const [isSearching, setIsSearching] = useState(false);

    const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    // Guarda de corrida: só a resposta da ÚLTIMA digitação pode atualizar a tela.
    const requestSeq = useRef(0);

    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    // Produtos visitados dependem de um store de navegação (ainda inexistente).
    const visitedProducts: VisitedProduct[] = [];

    async function fetchDropdown(query: string): Promise<{ terms: string[]; items: SearchResult[] }> {
        const storeId = getActiveStoreId();
        if (DROPDOWN_SOURCE === "suggest") {
            const data = await getSuggestions(query, storeId);
            return {
                terms: mergeSuggestedTerms(
                    data.terms ?? [],
                    data.categories.map((category) => category.name)
                ),
                items: data.products.map(toSearchResult),
            };
        }
        const data = await searchProducts({ q: query, storeId, perPage: DROPDOWN_PAGE_SIZE });
        return { terms: [], items: data.hits.map(toSearchResult) };
    }

    function handleChange(next: string) {
        setValue(next);
        onValueChange?.(next);
        onSearchResultsChange?.(null);
        clearTimeout(debounceTimer.current);

        if (!next.trim()) {
            setSuggestions([]);
            setResults(null);
            setIsSearching(false);
            return;
        }

        setIsOpen(true);
        setIsSearching(true);
        const seq = ++requestSeq.current;
        debounceTimer.current = setTimeout(async () => {
            try {
                const { terms, items } = await fetchDropdown(next);
                if (seq !== requestSeq.current) return; // resposta velha — descarta
                setSuggestions(terms);
                setResults(items);
                onSearchResultsChange?.(items);
            } catch {
                if (seq !== requestSeq.current) return;
                setSuggestions([]);
                setResults([]);
                onSearchResultsChange?.([]);
            } finally {
                if (seq === requestSeq.current) setIsSearching(false);
            }
        }, DEBOUNCE_MS);
    }

    function submit(term: string) {
        const trimmed = term.trim();
        if (!trimmed) return;
        add(trimmed);
        setIsOpen(false);
        router.push(searchHref(trimmed));
    }

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    const showDropdown = isOpen;

    return {
        value,
        setValue,
        isOpen,
        showDropdown,
        suggestions,
        results,
        isSearching,
        history,
        removeHistory: remove,
        refreshHistory,
        visitedProducts,
        handleChange,
        submit,
        open,
        close,
        router,
    };
}
